import {useEffect} from 'react';

import {batchUpdates} from '~ui/services/react';
import {instanceOf} from '~ui/utils';

import {useLatest} from './use-latest';

// eslint-disable-next-line max-params
export function useOutsideClick(
    elementRef: React.RefObject<HTMLElement>,
    handler: () => void,
    attached = true,
    ignoreSelectors?: string[],
    ignoreRefs?: React.RefObject<HTMLElement>[],
    eventType: 'click' | 'mousedown' = 'click',
    capture = false,
) {
    const latestHandler = useLatest(handler);
    const latestIgnoreSelectors = useLatest(ignoreSelectors);
    const latestIgnoreRefs = useLatest(ignoreRefs);

    useEffect(() => {
        if (!attached) return;

        const handleClick = (e: MouseEvent) => {
            const target = e.target;

            if (!instanceOf(target, HTMLElement)) {
                return;
            }

            if (!elementRef.current) {
                return;
            }

            if (!target.parentElement && target !== document.documentElement) {
                return;
            }

            if (elementRef.current.contains(target)) {
                return;
            }

            if (
                latestIgnoreSelectors.current &&
                latestIgnoreSelectors.current.length > 0 &&
                target.closest(latestIgnoreSelectors.current.join(', '))
            ) {
                return;
            }

            const shouldIgnoreByRef = latestIgnoreRefs.current?.some(({current}) => {
                return current && current.contains(target);
            });

            if (shouldIgnoreByRef) {
                return;
            }

            batchUpdates(() => {
                latestHandler.current();
            });
        };

        document.addEventListener(eventType, handleClick, {capture});

        return () => {
            document.removeEventListener(eventType, handleClick, {capture});
        };
    }, [elementRef, latestHandler, attached, capture, eventType]);
}
